/**
 * EyeHeights — the app's canonical eye/camera height constants.
 *
 * Phase 2 slice 2 (docs/architecture/PHASE2-SPATIAL-PLAN.md): these values were
 * scattered as magic literals across AutodeskViewer, WebIfcViewer,
 * WorkingE57PanoramaViewer, CoordinateSync and App. Values are unchanged.
 *
 * NOTE (load-bearing quirk): POI_SPHERE_STOREY_OFFSET_METERS (1.5) and
 * WALK_EYE_HEIGHT_METERS (1.7) are intentionally DIFFERENT values. Do not
 * reconcile them — POI sphere placement was tuned against 1.5 and changing it
 * shifts every POI icon on the panorama sphere (see plan NO-GO list).
 */

/**
 * Panorama capture eye height in Autodesk viewer units (feet — Revit models
 * are authored in feet; 5.5 ft ≈ 1.7 m person height). Panoramas are captured
 * at eye level above the floor slab, so storey classification and floor-index
 * building subtract this from camera Z to recover the slab Z.
 */
export const PANORAMA_EYE_HEIGHT_VIEWER_UNITS = 5.5;

/**
 * Default human eye level in meters for first-person walk navigation and
 * synced camera poses (IFC walk mode, coordinate sync, sphere navigation
 * camera-above-floor default).
 */
export const WALK_EYE_HEIGHT_METERS = 1.7;

/**
 * Offset in meters added to a storey's elevation to place the POI-visibility
 * camera height on the panorama sphere. Deliberately 1.5 (not the 1.7 walk eye
 * height) — an empirically tuned value; see the note at the top of this file.
 */
export const POI_SPHERE_STOREY_OFFSET_METERS = 1.5;

/**
 * Drop from a panorama's capture point (eye level) down to where its in-3D marker
 * billboard is placed in the Autodesk viewer (viewer units = feet). Deliberately
 * LESS than PANORAMA_EYE_HEIGHT_VIEWER_UNITS — a full drop to the slab read too
 * low; tuned by eye on staging (2026-07-13). A downward floor raycast could make
 * this exact later.
 *
 * 3D-VIEWER ONLY: the in-panorama nav icons are FLOOR-ANCHORED, not height-matched
 * (a floating height reads wrong inside a panorama — see
 * viewers/shared/panoramaNavPlacement.ts for the policy and rationale).
 */
export const PANORAMA_MARKER_FLOOR_DROP_VIEWER_UNITS = 3.5;
